/**
 * Setting or changing your own PIN, from the computer you work at.
 *
 * WHY THIS DID NOT EXIST ON THE WEB
 * ---------------------------------
 * The endpoint has always been there and the phone app has always used it. The
 * browser never called it — so a manager who approves discounts at the till,
 * on this screen, all day, could not set the PIN they approve them with. The
 * only PINs in the system were put there by a setup script.
 *
 * WHY THE PASSWORD IS ASKED FOR
 * -----------------------------
 * A PIN is four digits standing in for a password on a machine anybody can
 * walk up to. Letting whoever is already signed in change it without proving
 * who they are means a till left unlocked for two minutes is a till whose
 * approval PIN somebody else now knows — and every discount approved with it
 * afterwards carries the manager's name.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

const field =
  'h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none';

export function PinPanel({ pinSet, onClose }) {
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const [password, setPassword] = useState('');

  const save = useMutation({
    mutationFn: () => api.post('/auth/pin', { pin, password }),
  });

  // Typed twice, because nobody can read back what they set and a mistyped
  // PIN is only discovered at the moment somebody needs to approve something.
  const mismatch = again.length > 0 && pin !== again;
  const ready = pin.length >= 4 && pin === again && password.length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Change my PIN"
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/50 p-3 sm:items-center"
    >
      <div className="w-full max-w-sm rounded-2xl bg-surface p-5 shadow-xl">
        <h2 className="mb-1 font-medium text-ink">{pinSet ? 'Change my PIN' : 'Set a PIN'}</h2>
        <p className="mb-4 text-sm text-ink-2">
          Four digits, used to approve things at the till without signing in again.
        </p>

        {save.isSuccess ? (
          <>
            <p className="rounded-lg bg-good-bg px-3.5 py-2.5 text-sm text-good">
              Done. Use it the next time the till asks for an approval.
            </p>
            <div className="mt-4 flex justify-end">
              <Button onClick={onClose}>Close</Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-ink-2">New PIN</span>
                <input
                  type="password"
                  inputMode="numeric"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  aria-label="New PIN"
                  className={field}
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-ink-2">Again</span>
                <input
                  type="password"
                  inputMode="numeric"
                  value={again}
                  onChange={(e) => setAgain(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  aria-label="Again"
                  className={field}
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-ink-2">Your password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-label="Your password"
                  className={field}
                />
              </label>
            </div>

            {mismatch && <p className="mt-3 text-sm text-warn">Those two do not match.</p>}

            {save.isError && (
              <p className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
                {/* The branch refuses obvious PINs by name — 1234, 0000 — and
                    says so, which is more use than "invalid". */}
                {save.error?.message ?? 'That did not work.'}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!ready || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving' : 'Save'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
